import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Search, HardHat } from "lucide-react";
import { useLocaleFormat } from "@/lib/i18n-format";
import { useFilterByOwnerCompany } from "@/owner/OwnerCompanyContext";
import { CompanyBadge } from "@/owner/CompanyBadge";

export const Route = createFileRoute("/_app/projects/")({
  component: ProjectsPage,
});

type Status = "planning" | "active" | "on_hold" | "completed" | "cancelled";

type Row = {
  id: string;
  code: string;
  name: string;
  status: Status;
  budget: number;
  currency: string;
  start_date: string | null;
  end_date: string | null;
  company_id: string;
  clients: { name: string } | null;
};

const variant: Record<Status, "default" | "secondary" | "destructive" | "outline"> = {
  planning: "secondary",
  active: "default",
  on_hold: "outline",
  completed: "outline",
  cancelled: "destructive",
};

function ProjectsPage() {
  const { t } = useTranslation();
  const { formatMoney, formatDate } = useLocaleFormat();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("projects")
        .select(
          "id, code, name, status, budget, currency, start_date, end_date, company_id, clients(name)",
        )
        .order("created_at", { ascending: false });
      if (error) toast.error(error.message);
      setRows((data as unknown as Row[]) ?? []);
      setLoading(false);
    })();
  }, []);

  const scopedRows = useFilterByOwnerCompany(rows);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return scopedRows;
    return scopedRows.filter(
      (r) =>
        r.code.toLowerCase().includes(q) ||
        r.name.toLowerCase().includes(q) ||
        (r.clients?.name ?? "").toLowerCase().includes(q),
    );
  }, [scopedRows, search]);

  const fmt = (n: number, cur: string) => formatMoney(n, cur);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t("pages.projects.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("pages.projects.subtitle")}</p>
        </div>
        <Button onClick={() => navigate({ to: "/projects/new" })}>
          <Plus className="mr-2 h-4 w-4" />
          {t("pages.projects.new")}
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder={t("pages.projects.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      <div className="rounded-lg border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("pages.projects.headers.code")}</TableHead>
              <TableHead>{t("pages.projects.headers.client")}</TableHead>
              <TableHead>{t("pages.projects.headers.name")}</TableHead>
              <TableHead>{t("pages.projects.headers.status")}</TableHead>
              <TableHead className="text-right">{t("pages.projects.headers.budget")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-10 text-center text-muted-foreground">
                  {t("common.loading")}
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-16 text-center">
                  <div className="mx-auto flex max-w-sm flex-col items-center gap-2 text-muted-foreground">
                    <HardHat className="h-8 w-8" />
                    <p className="text-sm">{t("pages.projects.empty")}</p>
                    <Button size="sm" onClick={() => navigate({ to: "/projects/new" })}>
                      {t("pages.projects.createFirst")}
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((r) => (
                <TableRow key={r.id} className="cursor-pointer">
                  <TableCell className="font-mono text-xs">
                    <Link
                      to="/projects/$projectId"
                      params={{ projectId: r.id }}
                      className="hover:underline"
                    >
                      {r.code}
                    </Link>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.clients?.name ?? "—"}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <CompanyBadge companyId={r.company_id} />
                      <Link
                        to="/projects/$projectId"
                        params={{ projectId: r.id }}
                        className="font-medium hover:underline"
                      >
                        {r.name}
                      </Link>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant={variant[r.status]}>{t(`statuses.${r.status}`)}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {fmt(Number(r.budget), r.currency)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
